import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  AcceptPrivacyPolicyGQL,
  EducationInput,
  GetUserProfileGQL,
  GetUserProfileQuery,
  InitUserMatchesGQL,
  InterestInput,
  Profile,
  ProfileWorkTypeInput,
  SaveUserAirbaseLocationGQL,
  SaveUserMilitaryServeTypeGQL,
  SaveUserMilitaryTerminationGQL,
  SaveUserPreGoalsGQL,
  SaveUserProgramGoalsGQL,
  SaveUserSchoolGoalsGQL,
  SaveUserInstallationGQL,
  SaveUserSpacebaseLocationGQL,
  SaveUserWorkTerminationGQL,
  SkillsInput,
  UpdateInterestsGQL,
  UpdateLifestyleGQL,
  UpdateProfileWorkTypeGQL,
  UpdateSkillsGQL,
  UpdateValuesGQL,
  UserPreGoalInput,
  UserProgramGoalInput,
  UserSchoolGoalInput,
  UserTerminationInput
} from '@cpd-app/generated';

import {
  Aspirations,
  Location,
  UserInterest,
  UserProfile,
  UserWorkValue
} from '../../models';
import { ApolloQueryResult } from '@apollo/client/core';

@Injectable({
  providedIn: 'root'
})
export class UserProfileDataService {
  constructor(
    private getUserProfileGQL: GetUserProfileGQL,
    private updateValuesGQL: UpdateValuesGQL,
    private updateInterestsGQL: UpdateInterestsGQL,
    private updateLifestyleGQL: UpdateLifestyleGQL,
    private updateSkillsGQL: UpdateSkillsGQL,
    private saveUserPreGoalsGQL: SaveUserPreGoalsGQL,
    private acceptPrivacyPolicyGQL: AcceptPrivacyPolicyGQL,
    private updateProfileWorkType: UpdateProfileWorkTypeGQL,
    private initUserMatchesGQL: InitUserMatchesGQL,
    private saveUserMilitaryServeTypeGQL: SaveUserMilitaryServeTypeGQL,
    private saveUserAirbaseLocationGQL: SaveUserAirbaseLocationGQL,
    private saveUserSpacebaseLocationGQL: SaveUserSpacebaseLocationGQL,
    private saveUserInstallationGQL: SaveUserInstallationGQL,
    private saveUserProgramGoalsGQL: SaveUserProgramGoalsGQL,
    private saveUserSchoolGoalsGQL: SaveUserSchoolGoalsGQL,
    private saveUserWorkTransitionGQL: SaveUserWorkTerminationGQL,
    private saveUserMilitaryTransitionGQL: SaveUserMilitaryTerminationGQL
  ) {}

  public getUserProfile(): Observable<UserProfile> {
    // network-only (not Apollo's default cache-first) so a forced reload after
    // a server-side mutation -- e.g. invalidateUserProfile + loadUserProfile
    // dispatched from the JST overlay's saveToProfile success handler --
    // actually hits the server. Cache-first would return the pre-mutation
    // snapshot Apollo cached on app boot, masking the just-written data
    // (branchcode/moccode/userMocs/serveTypeId from prePopulateProfile).
    // The effect's withLatestFrom-filter still gates how often this fires,
    // so we don't spam the server -- only when state genuinely needs refresh.
    return this.getUserProfileGQL
      .fetch(undefined, { fetchPolicy: 'network-only' })
      .pipe(filter(Boolean), map(this.parseUserProfileData));
  }

  public getShareUserProfile(sessionToken?: string): Observable<UserProfile> {
    return this.getUserProfileGQL
      .fetch(
        { sessionToken },
        {
          fetchPolicy: 'no-cache'
        }
      )
      .pipe(map(this.parseUserProfileData));
  }

  public mapUserProfile(profileData: Profile): UserProfile {
    const profile: UserProfile = new UserProfile(profileData);

    return profile;
  }

  public saveUserSkillsSurvey(
    userProfile: UserProfile
  ): Observable<UserProfile> {
    const data: SkillsInput = {
      userId: userProfile.userId,
      profileType: +userProfile.profileType,
      isSkillsSurveyComplete: userProfile.isSkillsSurveyComplete,
      isInterestsSurveyComplete: userProfile.isInterestsSurveyComplete,
      isWorkValuesSurveyComplete: userProfile.isWorkValuesSurveyComplete,
      isLifestyleSurveyComplete: userProfile.isLifestyleSurveyComplete,
      isJstUploaded: userProfile.isJstUploaded,
      hasReturnUrl: userProfile.hasReturnUrl,
      returnUrlLastSavedDate: userProfile.returnUrlLastSavedDate,
      militaryBranch: userProfile.militaryBranch
        ? {
            branchCode: userProfile.militaryBranch.branchCode,
            branchTitle: userProfile.militaryBranch.branchTitle,
            branchLabel: userProfile.militaryBranch.branchLabel,
            mocSubstitute: userProfile.militaryBranch.mocSubstitute
          }
        : null,
      userRanks: (userProfile.userRanks || []).map((rank) => ({
        title: rank.title,
        mpcCode: rank.mpcCode,
        payGrade: rank.payGrade,
        branchCode: rank.branchCode,
        abbreviation: rank.abbreviation,
        payGradeLevel: rank.payGradeLevel
      })),
      userMocs: (userProfile.userMocs || []).map((moc) => ({
        status: moc.status,
        mocCode: moc.mocCode,
        mocTitle: moc.mocTitle,
        mpcCode: moc.mpcCode,
        branchCode: moc.branchCode,
        extendedBranchCode: moc.extendedBranchCode,
        numberOfMonthsOfWorkExperience: moc.numberOfMonthsOfWorkExperience
      })),
      hasCivilianWorkExperience: userProfile.hasCivilianWorkExperience
    };

    data.userWorkExperiences = userProfile.userWorkExperiences
      ? userProfile.userWorkExperiences.map((we) => {
          return {
            id: we.id,
            occupationTitle: we.occupationTitle,
            onetSocCode: we.onetSocCode,
            industryCode: we.industryCode,
            numberOfMonthsOfWorkExperience: we.numberOfMonthsOfWorkExperience
          };
        })
      : [];

    data.userEducations =
      userProfile.userEducations?.map((ue) => {
        const data: EducationInput = {
          educationLevelId: ue.educationLevelId,
          educationLevelShortDescr: ue.educationLevelShortDescr,
          valid: ue.valid
        };

        if (ue.degreeInfo) {
          data.degreeInfo = {
            id: ue.degreeInfo.id,
            cipCode: ue.degreeInfo.cipCode,
            title: ue.degreeInfo.title
          };
        }

        return data;
      }) || [];

    return this.updateSkillsGQL.mutate({ skills: data }).pipe(
      map((result) => {
        return this.mapUserProfile(result.data.updateSkills.profile);
      })
    );
  }

  public saveMilitaryServeType(serveTypeId: number): Observable<UserProfile> {
    return this.saveUserMilitaryServeTypeGQL.mutate({ id: serveTypeId }).pipe(
      map((result) => {
        return this.mapUserProfile(
          result.data.saveUserMilitaryServeType.profile
        );
      })
    );
  }

  public saveUserAirbaseLocation(
    airbaseLocationId: string
  ): Observable<UserProfile> {
    return this.saveUserAirbaseLocationGQL
      .mutate({ airbaseLocationId })
      .pipe(
        map((result) =>
          this.mapUserProfile(result.data.saveUserAirbaseLocation.profile)
        )
      );
  }

  // DATA-1: the all-branch replacement for saveUserAirbaseLocation /
  // saveUserSpacebaseLocation. Server-side the mutation is gated on
  // INSTALLATION_CAPTURE_ENABLED, so only call it behind the same flag.
  public saveUserInstallation(
    installationId: string
  ): Observable<UserProfile> {
    return this.saveUserInstallationGQL
      .mutate({ installationId })
      .pipe(
        map((result) =>
          this.mapUserProfile(result.data.saveUserInstallation.profile)
        )
      );
  }

  public saveUserSpacebaseLocation(
    spacebaseLocationId: string
  ): Observable<UserProfile> {
    return this.saveUserSpacebaseLocationGQL
      .mutate({ spacebaseLocationId })
      .pipe(
        map((result) =>
          this.mapUserProfile(result.data.saveUserSpacebaseLocation.profile)
        )
      );
  }

  public saveUserInterestsSurvey(
    userInterests: UserInterest[],
    trackerId: string
  ): Observable<UserProfile> {
    const interests: InterestInput[] = userInterests.map((i: UserInterest) => {
      return {
        interestId: i.interestId,
        level: i.level,
        abbreviation: i.abbreviation
      };
    });

    return this.updateInterestsGQL
      .mutate({
        interests: interests,
        trackerId
      })
      .pipe(
        map((result) => {
          return this.mapUserProfile(result.data.updateInterests.profile);
        })
      );
  }

  public saveUserWorkValues(
    userWorkValues: UserWorkValue[]
  ): Observable<UserProfile> {
    const values = userWorkValues.map((item: UserWorkValue) => {
      return {
        userId: null,
        workValueId: item.workValueId,
        level: item.level
      };
    });

    return this.updateValuesGQL
      .mutate({
        values: values
      })
      .pipe(
        map((result) => {
          return this.mapUserProfile(result.data.updateValues.profile);
        })
      );
  }

  public saveUserLifestyleSurvey(
    aspirations: Aspirations,
    preferredLocations: Location[],
    currentLocations: Location[]
  ): Observable<UserProfile> {
    const data = { preferredLocations, aspirations, currentLocations };
    return this.updateLifestyleGQL
      .mutate({
        lifestyle: data
      })
      .pipe(
        map((result) => {
          return this.mapUserProfile(result.data.updateLifestyle.profile);
        })
      );
  }

  public saveUsePreGoals(values: UserPreGoalInput): Observable<boolean> {
    return this.saveUserPreGoalsGQL
      .mutate({ values })
      .pipe(map((result) => result.data.saveUserPreGoals.success));
  }
  public saveUserProgramGoals(
    values: UserProgramGoalInput
  ): Observable<boolean> {
    return this.saveUserProgramGoalsGQL
      .mutate({ values })
      .pipe(map((result) => result.data.saveUserProgramGoals.success));
  }
  public saveUserSchoolGoals(values: UserSchoolGoalInput): Observable<boolean> {
    return this.saveUserSchoolGoalsGQL
      .mutate({ values })
      .pipe(map((result) => result.data.saveUserSchoolGoals.success));
  }

  public saveUserWorkTransition(
    values: UserTerminationInput
  ): Observable<UserProfile> {
    return this.saveUserWorkTransitionGQL
      .mutate({ values })
      .pipe(
        map((result) =>
          this.mapUserProfile(result.data.saveUserWorkTermination.profile)
        )
      );
  }

  public saveUserMilitaryTransition(
    values: UserTerminationInput
  ): Observable<UserProfile> {
    return this.saveUserMilitaryTransitionGQL
      .mutate({ values })
      .pipe(
        map((result) =>
          this.mapUserProfile(result.data.saveUserMilitaryTermination.profile)
        )
      );
  }

  public initUserMatches(): Observable<boolean> {
    return this.initUserMatchesGQL
      .fetch()
      .pipe(map((result) => result.data.initUserMatches.success));
  }

  public acceptPrivacyPolicy(values = false): Observable<{
    success: boolean;
    profile: UserProfile;
  }> {
    return this.acceptPrivacyPolicyGQL
      .mutate({ values })
      .pipe(map((result) => result.data.acceptPrivacyPolicy as any));
  }

  public acceptProfileWorkType(values: ProfileWorkTypeInput): Observable<{
    success: boolean;
    profile: UserProfile;
  }> {
    return this.updateProfileWorkType
      .mutate({ values })
      .pipe(map((result) => result.data.updateProfileWorkType as any));
  }

  private parseUserProfileData(
    results: ApolloQueryResult<GetUserProfileQuery>
  ): UserProfile {
    const normalizedUserProfile = {
      ...results?.data?.profile
    } as unknown as UserProfile;
    if (normalizedUserProfile.userId === 0) {
      normalizedUserProfile.hasCivilianWorkExperience = undefined;
    }
    return normalizedUserProfile;
  }
}
